import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

const isValidPostcode = (pc) => /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(pc.trim());
const money = (v) => (v == null || Number.isNaN(Number(v)) ? "—" : Number(v).toFixed(2));
const CENTRAL_PREFIXES = ["SW1", "SW3", "SW7", "W1", "WC1", "WC2", "EC1", "EC2", "EC3", "EC4", "NW1", "SE1"];

export default function ParkingVsTaxiLondon() {
  const [form, setForm] = useState({
    pickup_postcode: "",
    dropoff_terminal: "LHR5",
    passengers: 2,
    luggage: 2,
    days_parked: 5,
    pickup_datetime: ""
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const autoSubmitted = useRef(false);

  useEffect(() => {
    const nextWeekdayMorning = () => {
      const now = new Date();
      const day = now.getDay();
      const daysToMon = (8 - day) % 7 || 1;
      const target = new Date(now);
      target.setDate(now.getDate() + daysToMon);
      target.setHours(9, 0, 0, 0);
      const pad = (n) => String(n).padStart(2, "0");
      return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
    };

    setForm((prev) => ({
      ...prev,
      pickup_postcode: "SW1A 1AA",
      dropoff_terminal: "LHR5",
      passengers: 2,
      luggage: 2,
      days_parked: 5,
      pickup_datetime: nextWeekdayMorning()
    }));
  }, []);

  const submit = async () => {
    const pc = form.pickup_postcode.trim().toUpperCase();
    if (!pc || !isValidPostcode(pc)) {
      setError("Please enter a valid UK pickup postcode.");
      return;
    }
    const prefix = pc.replace(/\s+/g, "").slice(0, 3);
    const allowed = CENTRAL_PREFIXES.some((p) => pc.startsWith(p));
    if (!allowed) {
      setError(null);
      setResult(null);
      setNotice("⚠️ This page is optimised for Central London postcodes (SW1, SW3, SW7, W1, WC1, WC2, EC1–EC4, NW1, SE1). For other areas, use the main parking vs taxi calculator.");
      return;
    }
    if (!form.pickup_datetime) {
      setError("Please select a pickup date and time.");
      return;
    }

    setLoading(true);
    setError(null);
    setNotice(null);

    try {
      const payload = {
        ...form,
        pickup_postcode: pc,
        passengers: Math.min(6, Math.max(1, Number(form.passengers) || 2)),
        luggage: Math.min(6, Math.max(0, Number(form.luggage) || 2)),
        days_parked: Math.min(21, Math.max(1, Number(form.days_parked) || 5))
      };
      const res = await api.post("/api/parking/compare", payload);
      setResult(res.data);
    } catch (err) {
      setError("Failed to calculate comparison. Please try again.");
    }

    setLoading(false);
  };

  useEffect(() => {
    if (autoSubmitted.current) return;
    if (form.pickup_postcode && form.pickup_datetime) {
      autoSubmitted.current = true;
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickup_postcode, form.pickup_datetime]);

  const parkingTotal = result?.costs?.parking?.total ?? null;
  const taxiTwoWay = result?.costs?.taxi?.cheapcabgrab_two_way ?? null;
  const delta = parkingTotal != null && taxiTwoWay != null ? Number((parkingTotal - taxiTwoWay).toFixed(2)) : null;
  const heroVerdict = useMemo(() => {
    if (delta == null) return "Compare complete for this Central London → Heathrow trip.";
    if (Math.abs(delta) < 10) return "Costs are similar for this Central London trip — factor congestion, ULEZ, and fatigue.";
    if (delta < 0) {
      return `✅ Best choice for this Central London → Heathrow trip: Parking (about £${money(Math.abs(delta))} cheaper than two taxis). Taxi removes congestion/ULEZ admin and post-flight driving.`;
    }
    return `🚖 Best choice for this Central London → Heathrow trip: Taxi (about £${money(Math.abs(delta))} cheaper once congestion/ULEZ, fuel, and time are included).`;
  }, [delta]);

  const listStyle = { paddingLeft: 18, lineHeight: 1.7, color: "#1f2937", fontSize: 15, marginTop: 6, marginBottom: 6 };
  const sectionTitleStyle = { marginTop: 20, marginBottom: 8, fontSize: 18, fontWeight: 800, color: "#0f172a" };
  const bodyTextStyle = { fontSize: 15, color: "#1f2937", lineHeight: 1.6, marginTop: 4, marginBottom: 4 };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>Central London to Heathrow: Parking vs Taxi Cost Comparison (2026)</title>
        <meta
          name="description"
          content="Compare the real cost of parking vs taking a taxi from Central London to Heathrow. Includes congestion charge, ULEZ, fuel, parking, traffic delays, and travel stress — updated for 2026."
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/central-london-heathrow-parking-vs-taxi" />
        <meta property="og:title" content="Central London to Heathrow: Parking vs Taxi Cost Comparison (2026)" />
        <meta
          property="og:description"
          content="Is it cheaper to park or take a taxi from Central London to Heathrow? See live costs with congestion charge, ULEZ, fuel, parking, and fatigue included."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              { "@type": "Question", name: "Is it cheaper to take a taxi from Central London to Heathrow?", acceptedAnswer: { "@type": "Answer", text: "Often yes once congestion charge, ULEZ, fuel, and parking are included. This calculator shows the cheapest option for your postcode." } },
              { "@type": "Question", name: "Do I pay congestion charge if I drive to Heathrow?", acceptedAnswer: { "@type": "Answer", text: "If you drive through the zone during charging hours, £15 applies. The calculator factors this when relevant." } },
              { "@type": "Question", name: "Does ULEZ apply when driving to Heathrow Airport?", acceptedAnswer: { "@type": "Answer", text: "Non-compliant vehicles may owe £12.50 per day if entering the zone. We surface this as a hidden cost consideration." } },
              { "@type": "Question", name: "How much is parking at Heathrow for 5 days?", acceptedAnswer: { "@type": "Answer", text: "Varies by terminal and product. The calculator shows current pricing for your dates." } },
              { "@type": "Question", name: "Is parking worth it from Central London?", acceptedAnswer: { "@type": "Answer", text: "Often taxi wins once fees and stress are considered. Check your exact numbers above." } },
              { "@type": "Question", name: "What if my return flight is delayed?", acceptedAnswer: { "@type": "Answer", text: "Delays add parking time and fatigue. Taxi avoids post-flight driving and admin." } },
              { "@type": "Question", name: "Is a taxi better for early morning Heathrow flights?", acceptedAnswer: { "@type": "Answer", text: "Usually yes to avoid overnight parking, fatigue, and congestion-zone admin. The calculator compares both." } },
              { "@type": "Question", name: "Can a taxi avoid traffic better than driving myself?", acceptedAnswer: { "@type": "Answer", text: "Professional drivers and bus lanes can help; journey times remain variable. We factor typical traffic into the comparison." } }
            ]
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <div className="parking-header">
        <h1>Central London to Heathrow: Parking vs Taxi Cost Comparison (2026)</h1>
        <p style={{ fontSize: 15, color: "#0f172a", marginBottom: 6 }}>
          Congestion, ULEZ, traffic delays, and post-flight fatigue can make driving pricier than expected. Compare both options with live costs and hidden fees.
        </p>
        <p style={{ fontSize: 14, color: "#374151", marginBottom: 0 }}>
          Example calculation for Central London. Change the postcode if you’re elsewhere in Zone 1.
        </p>
      </div>

      <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f8fafc" }}>
        <div style={{ fontWeight: 800, fontSize: 18 }}>The quick answer for Central London</div>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", marginTop: 8 }}>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
            <div style={{ fontWeight: 800 }}>Use PARKING if:</div>
            <ul style={listStyle}>
              <li>You have ULEZ-compliant vehicle and accept congestion fees.</li>
              <li>You prioritise cost and don’t mind the return drive.</li>
              <li>You need your own car on return.</li>
            </ul>
          </div>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
            <div style={{ fontWeight: 800 }}>Use TAXI if:</div>
            <ul style={listStyle}>
              <li>You want to avoid congestion/ULEZ admin and PCN risk.</li>
              <li>You prefer zero post-flight driving fatigue.</li>
              <li>You value door-to-door reliability over parking shuttles.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="parking-form" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14, marginTop: 14 }}>
        <div className="form-group">
          <label>Pickup postcode (Central London prefilled, editable)</label>
          <input
            className="parking-input"
            value={form.pickup_postcode}
            onChange={(e) => setForm({ ...form, pickup_postcode: e.target.value })}
            placeholder="e.g. SW1A 1AA"
          />
        </div>

        {notice && (
          <div style={{ marginTop: 8, background: "#fff7ed", border: "1px solid #fed7aa", padding: 10, borderRadius: 10, color: "#9a3412" }}>
            {notice}{" "}
            <a href="/heathrow-parking-vs-taxi" style={{ fontWeight: 700, color: "#9a3412", textDecoration: "underline" }}>
              Open full parking vs taxi calculator →
            </a>
          </div>
        )}

        <div className="form-group">
          <label>Terminal</label>
          <select
            className="parking-input"
            value={form.dropoff_terminal}
            onChange={(e) => setForm({ ...form, dropoff_terminal: e.target.value })}
          >
            <option value="LHR2">Terminal 2</option>
            <option value="LHR3">Terminal 3</option>
            <option value="LHR4">Terminal 4</option>
            <option value="LHR5">Terminal 5</option>
          </select>
        </div>

        <div className="form-group">
          <label>Passengers</label>
          <input
            type="number"
            className="parking-input"
            min="1"
            max="6"
            value={form.passengers}
            onChange={(e) => setForm({ ...form, passengers: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Luggage</label>
          <input
            type="number"
            className="parking-input"
            min="0"
            max="6"
            value={form.luggage}
            onChange={(e) => setForm({ ...form, luggage: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Days parked (editable)</label>
          <input
            type="number"
            className="parking-input"
            min="1"
            max="21"
            value={form.days_parked}
            onChange={(e) => setForm({ ...form, days_parked: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Pickup date & time</label>
          <input
            type="datetime-local"
            className="parking-input"
            value={form.pickup_datetime}
            onChange={(e) => setForm({ ...form, pickup_datetime: e.target.value })}
          />
        </div>

        <button className="parking-btn" onClick={submit}>
          {loading ? "Calculating…" : "Compare parking vs taxi from Central London"}
        </button>

        {error && <p style={{ color: "red", marginTop: 8 }}>{error}</p>}
      </div>

      {result && (
        <div className="parking-results">
          <div
            style={{
              border: "1px solid #16a34a",
              borderRadius: 14,
              padding: 16,
              background: "#ecfdf5",
              boxShadow: "0 8px 20px rgba(22,163,74,0.08)"
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 18 }}>{heroVerdict}</div>
            <div style={{ marginTop: 6, fontSize: 14, color: "#1f2937" }}>
              Live calculation for {result.journey?.pickup_postcode} → {result.costs?.parking?.terminal || result.journey?.dropoff_terminal}. Includes fuel, parking, congestion/ULEZ context, taxi return, and fatigue indicators.
            </div>
            <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 8 }}>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
                <div style={{ fontWeight: 700 }}>Parking total</div>
                <div style={{ fontSize: 18 }}>{money(parkingTotal)}</div>
              </div>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 10, background: "#fff" }}>
                <div style={{ fontWeight: 700 }}>Two taxis (return)</div>
                <div style={{ fontSize: 18 }}>{money(taxiTwoWay)}</div>
              </div>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: "#475467" }}>
              Results vary by distance, passengers, congestion/ULEZ exposure, and return-journey fatigue.
            </div>
          </div>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Cost comparison: parking vs two taxi journeys</div>
            <table className="parking-table" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th>Option</th>
                  <th>Cost (£)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>🚗 Parking total</td>
                  <td>{money(result.costs?.parking?.total)}</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: 24, color: "#666" }}>└ Parking price ({result.costs?.parking?.pricing_mode || "—"})</td>
                  <td style={{ color: "#666" }}>{money(result.costs?.parking?.parking_price)}</td>
                </tr>
                <tr>
                  <td style={{ paddingLeft: 24, color: "#666" }}>└ Fuel estimate (return driving)</td>
                  <td style={{ color: "#666" }}>{money(result.costs?.parking?.fuel_price)}</td>
                </tr>
                <tr>
                  <td>🚖 CheapCabGrab (2 trips)</td>
                  <td>{money(result.costs?.taxi?.cheapcabgrab_two_way)}</td>
                </tr>
                <tr>
                  <td>📱 Ride-hail range (2 trips)</td>
                  <td>
                    {result.costs?.taxi?.ride_hail_two_way
                      ? `£${money(result.costs.taxi.ride_hail_two_way.min)} – £${money(result.costs.taxi.ride_hail_two_way.max)}`
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <td>🚕 Black cab range (2 trips)</td>
                  <td>
                    {result.costs?.taxi?.black_cab_two_way_range
                      ? `£${money(result.costs.taxi.black_cab_two_way_range.min)} – £${money(result.costs.taxi.black_cab_two_way_range.max)}`
                      : result.costs?.taxi?.black_cab_two_way != null
                        ? `~£${money(result.costs.taxi.black_cab_two_way)}`
                        : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: 13, color: "#374151" }}>
              Need other tools? <a href="/heathrow-parking-vs-taxi">Main parking vs taxi</a> • <a href="/7-day-heathrow-parking-vs-taxi">7-day</a> • <a href="/reading-heathrow-parking-vs-taxi">Reading</a> • <a href="/oxford-heathrow-parking-vs-taxi">Oxford</a> • <a href="/heathrow-dropoff-free">Free drop-off</a>.
            </div>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f9fafb" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Hidden costs of driving from Central London</div>
            <ul style={listStyle}>
              <li>Congestion Charge: £15/day if applicable.</li>
              <li>ULEZ: £12.50/day for non-compliant vehicles entering the zone.</li>
              <li>Fuel in stop-start traffic (higher than motorway rates).</li>
              <li>Parking shuttle time + risk of delays.</li>
              <li>Return journey fatigue after a long flight.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Why Central London is different</div>
            <ul style={listStyle}>
              <li>Heavy traffic at most hours; unpredictable journey times.</li>
              <li>High penalty risk (PCNs, missed congestion/ULEZ payments).</li>
              <li>Post-flight fatigue + long-haul arrivals make driving riskier.</li>
              <li>Fatigue/Convenience indicators matter more than raw price.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>When parking vs taxi</div>
            <div style={{ fontWeight: 700, marginTop: 6 }}>Parking usually wins if:</div>
            <ul style={listStyle}>
              <li>You’re ULEZ-compliant and accept congestion charges.</li>
              <li>You are price-sensitive and okay with post-flight driving.</li>
              <li>You need your own car immediately on return.</li>
            </ul>
            <div style={{ fontWeight: 700, marginTop: 10 }}>Taxi is often better if:</div>
            <ul style={listStyle}>
              <li>Early-morning or late-night flights (fatigue + admin risk).</li>
              <li>Family/elderly travellers — door-to-door over shuttles.</li>
              <li>You want zero congestion/ULEZ hassle and no PCN risk.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#f8fafc" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Real examples (Central London)</div>
            <ul style={listStyle}>
              <li>SW1 → T5: Taxi usually cheaper once congestion/ULEZ + parking are added; zero post-flight driving.</li>
              <li>WC2 → T3: Taxi often wins; parking savings small vs fees and time.</li>
              <li>EC2 → T2: Taxi convenience outweighs modest parking differences for many business travellers.</li>
            </ul>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Use the calculator for your exact trip</div>
            <p style={bodyTextStyle}>
              This page starts with a Central London example. Change the postcode, terminal, days, or date/time to personalise. For broader comparisons, visit the <a href="/heathrow-parking-vs-taxi">main tool</a>.
            </p>
            <p style={bodyTextStyle}>
              Other variants: <a href="/7-day-heathrow-parking-vs-taxi">7-day</a> • <a href="/reading-heathrow-parking-vs-taxi">Reading</a> • <a href="/oxford-heathrow-parking-vs-taxi">Oxford</a> • <a href="/heathrow-dropoff-free">Free drop-off</a>.
            </p>
          </div>

          <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 12, padding: 14, background: "#fff" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Central London FAQs</div>
            <div style={{ display: "grid", gap: 8 }}>
              {[
                { q: "Is it cheaper to take a taxi from Central London to Heathrow?", a: "Often yes once congestion charge, ULEZ, fuel, and parking are included. Check the live comparison above." },
                { q: "Do I pay congestion charge if I drive to Heathrow?", a: "If you pass through the charging zone during hours, yes (£15/day). Avoiding the zone may add time. The calculator notes this as a cost factor." },
                { q: "Does ULEZ apply when driving to Heathrow Airport?", a: "Non-compliant vehicles owe £12.50/day when entering the zone. This is surfaced as a hidden cost consideration." },
                { q: "How much is parking at Heathrow for 5 days?", a: "Varies by terminal/product; we show current pricing for your dates and time." },
                { q: "Is parking worth it from Central London?", a: "Taxi often wins once fees and fatigue are considered, but your exact numbers are shown above." },
                { q: "What if my return flight is delayed?", a: "Parking time may extend and fatigue rises. Taxi avoids post-flight driving." },
                { q: "Is a taxi better for early morning Heathrow flights?", a: "Often yes — avoids overnight parking and congestion/ULEZ admin when tired." },
                { q: "Can a taxi avoid traffic better than driving myself?", a: "Professional drivers and bus lanes can help, but traffic is still variable. We factor typical conditions in the comparison." }
              ].map(({ q, a }) => (
                <details key={q} className="train-collapsible" open={false}>
                  <summary className="train-summary-bar" style={{ fontWeight: 700, fontSize: 15 }}>{q}</summary>
                  <div className="train-collapsible-body" style={{ fontSize: 14, color: "#1f2937" }}>{a}</div>
                </details>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
            <strong>Plan your Heathrow journey:</strong>
            <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
              <li>🚖 <a href="/heathrow-fare-compare">Compare taxi fares</a></li>
              <li>🚆 <a href="/train-vs-taxi">Train vs taxi</a></li>
              <li>🅿️ <a href="/heathrow-parking-vs-taxi">Parking vs taxi</a></li>
              <li>✈️ <a href="/flight-checker">Check your flight</a></li>
              <li>🕒 <a href="/leave-for-heathrow">When to leave for Heathrow</a></li>
              <li>🧭 <a href="/terminalFinder">Find your terminal</a></li>
              <li>🧳 <a href="/arrival-assistant">Arrival assistant</a></li>
            </ul>
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
            Last updated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
          </div>
        </div>
      )}

      <div style={{ marginTop: 30 }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
